import { useCallback, useEffect, useState } from "react";

import apiV2 from "../../../services/apiV2";

export default function useSocialComments({
	channel,
	status,
	search,
	page,
	setHasMore,
}) {
	const [loading, setLoading] = useState(true);
	const [loadingMoreComments, setLoadingMoreComments] = useState({});
	const [error, setError] = useState(null);
	const [postGroups, setPostGroups] = useState([]);

	const load = useCallback(async () => {
		if (page === 1) {
			setLoading(true);
		}
		setError(null);

		try {
			const { data } = await apiV2.post("/socialMedia", {
				channel,
				status,
				search,
				page,
			});

			const incoming = Array.isArray(data.posts) ? data.posts : [];

			setPostGroups((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
			setHasMore(Boolean(data.hasMore));
		} catch (err) {
			setError(err);
		} finally {
			setLoading(false);
		}
	}, [channel, status, search, page, setHasMore]);

	useEffect(() => {
		load();
	}, [load]);

	const replyToComment = useCallback((id, text) => {
		const trimmed = String(text || "").trim();
		if (!trimmed) return false;

		setPostGroups((prev) =>
			prev.map((group) => ({
				...group,
				comments: group.comments.map((comment) => {
					if (comment.id !== id) return comment;
					const reply = {
						id: `r_${Date.now()}`,
						author: { name: "Atendimento", avatarUrl: null },
						body: trimmed,
						createdAt: new Date().toISOString(),
						isAgent: true,
					};
					return {
						...comment,
						status: "replied",
						replies: [...(comment.replies || []), reply],
					};
				}),
			}))
		);
		return true;
	}, []);

	const updateCommentStatus = useCallback(async (id, nextStatus) => {
		try {
			await apiV2.put("/socialMedia", {
				commentId: id,
				status: nextStatus,
			});

			setPostGroups((prev) =>
				prev.map((group) => ({
					...group,
					comments: group.comments.map((comment) =>
						comment.id === id ? { ...comment, status: nextStatus } : comment
					),
				}))
			);
		} catch (err) {
			console.error(err);
		}
	}, []);

	const loadMoreComments = useCallback(
		async (postId) => {
			if (!postId || loadingMoreComments[postId]) return;

			const group = postGroups.find((item) => item.post?.id === postId);
			if (!group?.hasMoreComments) return;

			const nextPage = (group.commentsPage || 1) + 1;

			setLoadingMoreComments((prev) => ({ ...prev, [postId]: true }));
			try {
				const { data } = await apiV2.post("/socialMedia/comments", {
					postId,
					channel,
					status,
					search,
					page: nextPage,
				});

				const incoming = Array.isArray(data.comments) ? data.comments : [];

				setPostGroups((prev) =>
					prev.map((item) => {
						if (item.post?.id !== postId) return item;

						const existingIds = new Set(item.comments.map((c) => c.id));
						const merged = [
							...item.comments,
							...incoming.filter((c) => !existingIds.has(c.id)),
						];

						return {
							...item,
							comments: merged,
							commentsTotal: data.commentsTotal ?? item.commentsTotal,
							hasMoreComments: Boolean(data.hasMoreComments),
							commentsPage: data.page || nextPage,
						};
					})
				);
			} catch (err) {
				console.error(err);
			} finally {
				setLoadingMoreComments((prev) => ({ ...prev, [postId]: false }));
			}
		},
		[channel, status, search, postGroups, loadingMoreComments]
	);

	return {
		loading,
		error,
		postGroups,
		replyToComment,
		updateCommentStatus,
		loadMoreComments,
		loadingMoreComments,
		reload: load,
	};
}
